// Written trainings: the main content for training steps that used to be video-only.
// Written from each video's transcript (cleaned up and modernized), keyed by step id.
// Shape: { intro, steps: [..], tips: [..] }. The video stays below as an optional "Watch video".
// "Resi's YouTube Studio" is Resi's own tool (Marketing > YouTube Studio, coming soon for agents).
// "YouTube Studio on YouTube" means studio.youtube.com, where you upload and manage videos.

const RESI_YTS = "Resi's YouTube Studio (coming soon for agents) will do this for you: it researches topics from real search demand, writes the script in your voice, and gives you title options, three thumbnail options, the description, tags and hashtags. Until it's open to you, use the AI prompts below.";

export const writtenTrainings = {
  // ---------- YouTube ----------
  'youtube-channel-setup-playlists': {
    intro: 'Playlists organize your channel so viewers keep watching, and a playlist with a keyword-rich title and description is one more way to show up in search.',
    steps: [
      'Open YouTube Studio on YouTube (studio.youtube.com) and click Create, then New playlist.',
      'Start with six core playlists: Buyer Tips, Seller Tips, Market Updates, Community Tours, Property Tours, and Relocation. Add a playlist for a farm area or submarket only if you make a lot of content there.',
      'Put your city in each title, for example "Estero, FL Real Estate Market Updates" instead of "Market Updates."',
      'Set each playlist to Public and click Create.',
      'Go to Content, then Playlists, and open each playlist to edit it.',
      'Add a description (up to 5,000 characters) that uses your keywords, for example: "All the latest updates on the Estero, FL real estate market: buyer tips, seller tips, and what interest rates are doing." Click Save.',
    ],
    tips: [
      'Keep the list short. Too many choices make viewers pick nothing.',
      'Use AI to write your playlist descriptions: paste your city, your communities and the playlist topic, and ask for a two-sentence description with local keywords.',
    ],
  },
  'youtube-channel-setup-home-screen': {
    intro: 'Your channel homepage should steer visitors to the videos that bring you business. Sections built from your playlists make the channel look complete and keep people watching.',
    steps: [
      'Go to your channel and click Customize channel.',
      'Under Featured sections, click Add section.',
      'Add a Popular videos section so new visitors see what is already working.',
      'Add Single playlist sections for the playlists you most want watched, such as your market updates or a community tour playlist.',
      'Use Multiple playlists if you want several related playlists in one row.',
      'Drag the sections by their handles to set the order. Most agents put popular videos first. Click Publish.',
    ],
    tips: [
      'Ask yourself what you want a new visitor to watch next, and put that near the top.',
      'Community tours and property tours tend to bring in the most clients.',
    ],
  },
  'youtube-channel-setup-channel-tags': {
    intro: 'Channel keywords tell YouTube what your whole channel is about. Few agents set them, and it only takes a few minutes.',
    steps: [
      'In YouTube Studio on YouTube, click Settings at the bottom left.',
      'Click Channel, then find the Keywords field.',
      'Add your name, your market (for example "moving to Estero FL", "living in Bonita Springs"), and the communities you serve.',
      'Click Save.',
    ],
    tips: [
      "Don't overthink it. Your videos decide how your channel performs; keywords just give YouTube a starting point.",
      'Ask AI for a list: give it your name, cities and communities, and ask for 20 channel keywords people would search.',
    ],
  },
  'youtube-channel-setup-featured-videos': {
    intro: 'The video spotlight plays at the top of your channel. You can show one video to people who are not subscribed yet and a different one to returning subscribers.',
    steps: [
      'Go to your channel and click Customize channel.',
      'On the Layout tab, find Video spotlight.',
      'For the Channel trailer (non-subscribers), click Change video and pick the video that best introduces you.',
      'For the Featured video for returning subscribers, pick your most recent video.',
      'Click Publish.',
    ],
    tips: [
      'Each time a new video goes live, make it the featured video for returning subscribers so they see it.',
      'You need a few videos posted before this step is worth doing.',
    ],
  },
  'youtube-optimizing-banner-channel-art': {
    intro: 'Your banner (channel art) is free advertising at the top of your channel. Design it for phones first, because most of your viewers watch on mobile.',
    steps: [
      'Create the banner at YouTube\'s banner size (2560 x 1440). Keep your photo, name and main message in the center safe area. That center strip is all that shows on phones; the sides only show on desktop and TV.',
      'Say who you help and where, for example "Helping buyers and sellers in Estero and Bonita Springs," and add your upload schedule if you have one.',
      'To upload: go to your channel, click Customize channel, then the Branding tab.',
      'Next to Banner image, click Change, pick your file, and click Publish. If you already have a banner, you can also hover over it and click the camera icon.',
      'Change your profile picture on the same Branding tab.',
    ],
    tips: [
      'You can design the banner with an AI image tool: describe the layout, give it the exact text and the RESIDENCE colors (black, white and gold), and ask for 2560 x 1440. Check every word for spelling before you upload.',
      'Look at the banner on your phone after you publish to make sure nothing important is cut off.',
    ],
  },
  'youtube-10-45-45-rule': {
    intro: 'The 10-45-45 rule: only about 10% of how your message lands is the exact words. About 45% is how you say it and 45% is your body language. You don\'t need perfect words to make great videos.',
    steps: [
      'Words (10%): don\'t re-record because you stumbled on a word. Get the point across and keep going.',
      'Voice (45%): speak with confidence, emphasis and conviction. Change your tone and pace so you\'re never monotone.',
      'Body language (45%): smile, look into the lens, and use your hands. Don\'t stand stiff.',
      'Talk about what you know. Confidence comes from experience, so learn your market and your communities before you film them.',
    ],
    tips: [
      'If you have an accent or speak slowly, that\'s fine. Energy and warmth matter far more.',
      'Have fun on camera. Viewers connect with a real person, not a perfect one.',
    ],
  },
  'youtube-planning-first-6-weeks': {
    intro: 'Plan your content before you film so you never sit down wondering what to make. Start with proven topic types for your first videos, then build from what works.',
    steps: [
      RESI_YTS,
      'Without it yet: ask AI for topic ideas. Give it your city, your communities and the buyers and sellers you serve, and ask for 20 video topics people actually search for, each with a one-line angle.',
      'Mix the core types: market updates, community tours, property tours, relocation ("moving to"), cost of living, and buyer and seller tips. "Five reasons to move to [your city]" is an easy first video.',
      'Put your plan in a simple list or Google Sheet: topic, title, filming date, posting date. You don\'t need special software.',
      'Plan about two videos a week, which is roughly 12 videos for your first six weeks.',
      'After your first batch, look at which topics got the most views and plan more like them.',
    ],
    tips: [
      'Real client questions make the best topics. Paste the questions you hear most into AI and ask it to turn them into video titles.',
    ],
  },
  'youtube-recording-from-outline': {
    intro: 'Record from an outline, not a word-for-word script. Bullet points keep you on track and still sound natural.',
    steps: [
      'Write the video title at the top of a page.',
      'Under it, list your main points as short bullets (for example, five reasons to move to your city, one line each).',
      RESI_YTS + ' You can use the script as your guide and talk from its main points.',
      'Without it yet: ask AI for an outline. Give it the title and who the video is for, and ask for an opener, five short talking points, and a closing call to action.',
      'Put the bullets where you can glance at them, then talk through each point in your own words.',
      'If you lose your place, pause, look at the next bullet, and keep going. You can cut the pause when you edit.',
    ],
    tips: [
      'Reading a full script usually sounds stiff. Talking from bullets sounds like you.',
      'Only talk about things you know well. That\'s what makes you believable.',
    ],
  },
  'youtube-bigvu-scripts-teleprompter': {
    intro: 'If you prefer reading on camera, a teleprompter app such as BIGVU lets you write or paste a script and read it while you record on your phone.',
    steps: [
      'In BIGVU, tap the plus icon to start a new video.',
      'Paste your script (for example the one from Resi\'s YouTube Studio when it opens to you, or your own AI script), or use the app\'s AI writer to draft one.',
      'Tap Shoot to open the teleprompter.',
      'Set the scroll speed and font size so your eyes stay near the lens. Many people start around 150 to 200 words per minute; test what feels natural.',
      'Lock the exposure so the lighting doesn\'t shift while you record, and set front camera quality to 1080p.',
      'Record, then add captions, your logo or overlays in the app if you want.',
    ],
    tips: [
      'Practice the script out loud once first and change any line you wouldn\'t naturally say.',
    ],
  },
  'youtube-capcut-editing': {
    intro: 'CapCut is the editor we use for videos, Shorts and Reels. It\'s free on your phone and computer.',
    steps: [
      'Open CapCut, tap New project, and add your clips in order.',
      'Trim the start and end, and cut out long pauses and mistakes.',
      'Use Auto captions to add captions, then read through them and fix any wrong words.',
      'Add your logo, text overlays, and background music at a low volume.',
      'Export in 1080p (or 4K if you recorded in 4K).',
      'Turn long videos into Shorts and Reels: pick your strongest 30 to 60 seconds, set the canvas to 9:16, reframe so you stay centered, add captions, and export.',
    ],
    tips: [
      'Resi\'s YouTube Studio (coming soon for agents) also writes a companion reel script for each video and puts a Produce reel task on your calendar.',
      'Ask AI to pick the best moments: paste your video\'s captions and ask for the three strongest 45-second clips for Shorts.',
    ],
  },
  'youtube-upload-first-video': {
    intro: 'Upload first, optimize second. Save the video as unlisted so it isn\'t public until the title, description and tags are done.',
    steps: [
      'In YouTube Studio on YouTube, click Create, then Upload videos.',
      'Drag in your video file or click Select files.',
      'While it uploads, add your thumbnail and add the video to a playlist. Skip the rest for now.',
      'Set visibility to Unlisted and save.',
      'When processing finishes (check Content), go back in and finish the title, description, tags and end screen, then schedule it to go public.',
    ],
    tips: [
      'Taking a few extra minutes on the details after upload helps your videos rank.',
    ],
  },
  'youtube-title-optimization': {
    intro: 'Your title decides whether people find your video and click it. Be literal and search-friendly, not clever. AI is a big help here.',
    steps: [
      'Make sure the video is still Unlisted while you work on it.',
      'Search YouTube for your topic (for example "moving to Estero FL") and look at the titles already ranking. Note which are out of date.',
      'Write a title that is literal and includes the place and the year, for example "Moving to Estero, FL in 2027: 5 Things You Need to Know."',
      'Add a curiosity hook if it fits, such as "that nobody talks about."',
      RESI_YTS,
      'Without it yet: paste your topic and the top-ranking titles into AI and ask for 10 title options under 60 characters with the city and year. Pick one and refine it.',
      'Then fill in the tags and description, and add the end screen.',
    ],
    tips: [
      'Others have already done the research. Find what ranks and make yours more current and more specific.',
      'Keep the important words at the start, because titles get cut off on phones.',
    ],
  },
  'youtube-adding-end-screen': {
    intro: 'An end screen recommends another video and a subscribe button in the last seconds, which keeps viewers on your channel.',
    steps: [
      'Open the video in YouTube Studio on YouTube, go to Details, and click End screen.',
      'Choose a template with a subscribe element and a video element.',
      'For the video element, choose Specific video and pick the logical next video. For example, after "5 reasons to move to Estero," show your cost of living video.',
      'If you don\'t have a good match yet, choose Best for viewer.',
      'Drag the elements where you want them and click Save.',
      'Say a call to action at the end of the video, such as "Subscribe for more on the Estero real estate market."',
    ],
    tips: [
      'Leave 10 to 20 seconds at the end of each video for the end screen.',
    ],
  },
  'youtube-batching-content': {
    intro: 'Batching means filming several videos in one session so you stay a couple of weeks ahead, even when work gets busy.',
    steps: [
      'Pick three to four topics from your plan.',
      'Get an outline or script ready for each one before filming day.',
      'Set up once (your phone and natural light is enough) and record all of them back to back.',
      'Edit in CapCut, upload as unlisted, and schedule them a week apart.',
    ],
    tips: [
      'Resi\'s YouTube Studio (coming soon for agents) schedules this for you: it puts Record (Mondays), Post (Wednesdays) and a reel task on your calendar.',
      'Change your shirt between videos so each one looks fresh.',
    ],
  },
  'youtube-improving-content-over-time': {
    intro: 'YouTube is a long game. Your goal is for every video to be a little better than the last.',
    steps: [
      'Search for a topic you want to rank for and watch the top three videos.',
      'Watch your own video on that topic and be honest: is yours as engaging? Where does your attention drift?',
      'In YouTube Studio on YouTube, check Analytics for each video: views, average view duration, and where viewers drop off.',
      'Fix the weak spots in your next video, such as a slow opening, a long section, or a weak title.',
      'Make more of what works. Paste your top and bottom videos\' titles and stats into AI and ask what the best ones have in common.',
    ],
    tips: [
      'Repurpose every video: ask AI to turn the captions into a blog post, a Facebook post, and an email to your database.',
    ],
  },
  'youtube-ai-titles-tags-descriptions-thumbnails': {
    intro: 'Use AI for as much of the YouTube work as you can. Resi\'s YouTube Studio is coming soon for agents and will handle most of it in one place. Until then, use these steps with ChatGPT or Claude.',
    steps: [
      'Topics and research: ' + RESI_YTS,
      'Topic ideas without it: give AI your city, communities and audience, and ask for 20 topics people search for. Paste in real client questions for even better ideas.',
      'Titles: paste your topic and a few top-ranking titles and ask for 10 search-friendly titles under 60 characters that include the city and year.',
      'Scripts and outlines: paste two or three of your past posts or captions so the AI learns your voice, then ask for an outline (opener, main points, call to action) before the full script.',
      'Thumbnails: make them with AI instead of Canva. Describe the image, give the text (four words or fewer) and the RESIDENCE colors, and use your own photo. Resi\'s YouTube Studio will give you three thumbnail options per video.',
      'Descriptions, tags and chapters: paste your script or captions and ask for a description with a two-sentence summary and a call to action to call or text Kelly at 239-488-5900, 15 tags, and chapter timestamps.',
      'Captions: use CapCut\'s Auto captions or YouTube\'s automatic captions, then read through them and fix any wrong words.',
      'Shorts and Reels: paste the captions and ask AI for the three strongest 30 to 60 second moments, then cut them in CapCut at 9:16.',
      'Repurposing: paste the captions and ask for a blog post, a Facebook post, an Instagram caption and an email to your database.',
    ],
    tips: [
      'Add your own local story or a number from your market report. That\'s what makes AI content sound like you.',
      'Read titles, descriptions and numbers once before you publish.',
    ],
  },
  // ---------- Open houses ----------
  'choose-open-house-video': {
    intro: 'Not every open house is worth your Saturday. Choose ones that put you in front of the buyers you want to work with.',
    steps: [
      'Start with the team. Our agents always have listings that need a host. Finish this open house training, then let Kelly know you are ready.',
      'In Resi, open Open Houses to see the team\'s open houses and click I will host on one that fits.',
      'Match the house to your niche. If you want first-time buyers, host in communities and price ranges first-time buyers shop.',
      'Host in your farm area so neighbors start to know you as the local agent.',
      'Start with eXp listings, then other agents\' listings in your niche or farm area.',
    ],
    tips: ['Pick houses you\'d be proud to show, with good traffic and a fair price.'],
  },
  'reach-out-book-open-houses-video': {
    intro: 'No listings yet? Ask listing agents in your niche or farm area if you can host their open house. Many agents are happy to say yes.',
    steps: [
      'Search the MLS for homes for sale in your niche or farm area.',
      'Open the listing and find the listing agent\'s contact information.',
      'Call them. For example: "Hi John, it\'s [your name] with RESIDENCE | eXp Realty. I saw your listing at 1234 Main Street. I\'m looking for more open house experience and I\'ve finished our open house training. Could I host it for about two hours this Saturday or Sunday?"',
      'If they say no, thank them and wish them luck with the listing.',
      'If they say yes, ask permission to market the open house with their photos, description and any listing video.',
      'Get that permission in writing: email them and ask them to reply confirming.',
      'Always name them as the listing agent, with their brokerage, in your marketing.',
    ],
    tips: ['Have two dates ready when you call so it\'s easy to say yes.'],
  },
  'prepare-open-house-video': {
    intro: 'Start preparing a week before the open house: get the assets, market it, and have your sign-in and follow-up plan ready.',
    steps: [
      'Collect the photos from the MLS and the listing video from the listing agent if there is one.',
      'Study the listing and pick the top three features that make the home stand out, such as the view, the pool, upgrades, or the price.',
      'Make a short teaser video. Go on camera if you can ("I\'m hosting an open house next Saturday from 1 to 3 at ..."), or make a photo slideshow with a voiceover in CapCut.',
      'Post during the week: the teaser, reminders, and photos. Say you\'re hosting; never say you\'re the listing agent.',
      'Link to a squeeze page, never straight to the MLS listing, so interested people land in your database. See the squeeze page training in Lead Generation.',
      'Optional: run a small Facebook and Instagram ad ($3 to $5 a day for five to seven days) using the same video. See the paid ads training.',
      'Print sign-in sheets that ask about timing and whether they\'re working with an agent. Paper sign-in often works better than an iPad here.',
      'Make a flyer for the home with your contact information.',
      'Pack your go bag: signs, sign-in sheets, pens, flyers, a speaker, and air freshener.',
    ],
    tips: ['Use AI to write your teaser script and social captions: paste the listing description and your three top features.'],
  },
  'day-of-follow-up-video': {
    intro: 'On the day, set the house up to shine, greet every guest, and follow up with every one of them.',
    steps: [
      'Dress for the clients you want. Arrive at least 15 minutes early, and make sure you have access (and your name at the gate if it\'s gated).',
      'Check the community\'s sign rules, then put out as many directional signs as allowed. Park a few doors down so you don\'t block the house.',
      'Turn on every light, open blinds and drapes, put away personal items on counters, unlock doors and sliders, and start background music.',
      'Set the sign-in sheet, flyers and pens on the kitchen island.',
      'Greet everyone at the door. Open with an easy question such as "Are you familiar with the community?"',
      'Give a quick overview of the home and its top three features, then let them explore. Ask open questions: who will live there, how soon they want to move, full-time or part-time.',
      'Ask "Have you signed a buyer broker agreement with an agent?" rather than "Do you have an agent?"',
      'Use quiet time to work: film content, make calls.',
      'Afterward, add every guest to your database in Resi (Leads, then Contacts, then + Add contact) with notes from your conversation, and follow up within a day.',
      'Keep in touch every couple of weeks and offer to show similar homes. Let the listing agent know how the open house went.',
    ],
    tips: [
      'Some open houses will have six groups and some none. Consistency and follow-up are what make them work.',
      'Ask AI to draft your follow-up texts: paste your notes on each guest.',
    ],
  },
  // ---------- BoldTrail (formerly KV Core) ----------
  'create-squeeze-page': {
    intro: 'A squeeze page is a trackable BoldTrail link to one property or a search of properties. When someone enters their information to see more, they go into your BoldTrail database tagged with a hashtag you choose.',
    steps: [
      'In BoldTrail (formerly KV Core), go to Lead Engine Tools and click Build Squeeze Page. You can also do this in the BoldTrail app on your phone.',
      'Choose Multi Property for a search (for example, homes with pools in Estero under $500,000) or Single Property for one listing.',
      'For Multi Property, set the location (draw on the map, type a city, or pick a community), listing type, price and other filters.',
      'Add a hashtag so you know where the lead came from and so it can trigger a follow-up campaign.',
      'Save the page and copy the link.',
      'Use that link in every post, ad and open house promotion instead of a plain MLS link.',
    ],
    tips: ['Never post a link to a home without a squeeze page, so no interested buyer slips by.'],
  },
  'create-landing-page': {
    intro: 'A landing page delivers something (like a guide) in exchange for contact information, and puts the person in your BoldTrail database.',
    steps: [
      'Get a share link for what you\'re delivering, for example a public view link from Canva for your guide.',
      'In BoldTrail, go to Lead Engine Tools and choose Build Landing Page.',
      'Choose a lead generation layout. You can add a short intro video if you want.',
      'Add a hashtag for this guide (for example #myguide) so you can track it and trigger a follow-up campaign.',
      'Add the guide link as what people get after they sign up, then save and copy the page link.',
    ],
    tips: ['Landing pages are hard to edit after you save them, so check everything before you finish. You may need to redo one.'],
  },
  'automatic-follow-up-campaign': {
    intro: 'Smart campaigns in BoldTrail send automatic follow-up to leads based on their hashtag, for example every lead tagged #buyer.',
    steps: [
      'In BoldTrail, go to Marketing, then Smart Campaigns.',
      'Start with a general buyer campaign that runs when the hashtag is buyer.',
      'Add touches: for example emails on day 1, 3, 4, 5 and 7, such as a welcome email with a video introducing you and an email about the home buying process.',
      'Use the same hashtag on your squeeze pages so new leads get the campaign automatically.',
      'Ask Kelly if you want a copy of her campaigns; they can be cloned or shared to you.',
    ],
    tips: ['Include video in your emails. Seeing you helps leads get to know and trust you.'],
  },
};
writtenTrainings['lead-magnets-landing-page'] = writtenTrainings['create-landing-page'];
